import { useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";

import { HubDataTable, type HubDataTableColumn } from "@/components/hub/HubDataTable";
import { PageTitleWithHelp } from "@/components/ui/title-help";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { Input, Label } from "@/components/ui/Input";
import type { SortDirection } from "@/components/ui/SortableTableHeader";
import { cn } from "@/lib/utils";
import {
  fetchSuppliers,
  type MasterDataSupplierRow,
  type SupplierListStatus,
} from "@/lib/masterData";
import { SupplierDetailPanel } from "./SupplierDetail";

type SupplierSortKey = "name";

const STATUS_OPTIONS: { value: SupplierListStatus; label: string }[] = [
  { value: "active", label: "Active" },
  { value: "deleted", label: "Deleted" },
  { value: "all", label: "All" },
];

function compareText(left: string, right: string): number {
  return left.localeCompare(right, undefined, { sensitivity: "base" });
}

function sortRows(
  rows: MasterDataSupplierRow[],
  _sortKey: SupplierSortKey,
  sortDirection: SortDirection,
): MasterDataSupplierRow[] {
  const direction = sortDirection === "asc" ? 1 : -1;
  return [...rows].sort((left, right) => {
    const comparedName = compareText(left.mv_supplier_name, right.mv_supplier_name);
    const compared = comparedName !== 0 ? comparedName : left.mv_supplier_id - right.mv_supplier_id;
    return compared * direction;
  });
}

export function SupplierListPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedRaw = Number.parseInt(searchParams.get("supplier") ?? "", 10);
  const selectedSupplierId = Number.isFinite(selectedRaw) ? selectedRaw : null;
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState<SupplierListStatus>("active");
  const [sortKey, setSortKey] = useState<SupplierSortKey>("name");
  const [sortDirection, setSortDirection] = useState<SortDirection>("asc");
  const { data, isLoading, error } = useQuery({
    queryKey: ["mrp-suppliers", query, status],
    queryFn: () => fetchSuppliers({ q: query, status }),
  });

  function selectSupplier(supplierId: number) {
    setSearchParams((current) => {
      const next = new URLSearchParams(current);
      next.set("supplier", String(supplierId));
      return next;
    });
  }

  const columns = useMemo((): HubDataTableColumn<MasterDataSupplierRow, SupplierSortKey>[] => {
    return [
      {
        id: "name",
        header: "Supplier",
        sortKey: "name",
        sortValue: (row) => row.mv_supplier_name,
        cell: (row) => (
          <span className="inline-flex flex-wrap items-center gap-1.5">
            <span className="font-medium text-slate-900">{row.mv_supplier_name}</span>
            {row.megaventory_deleted_at != null ? <Badge tone="muted">Inactive</Badge> : null}
            {row.current_user_follows ? <Badge tone="info">Following</Badge> : null}
          </span>
        ),
      },
    ];
  }, []);
  const rows = useMemo(
    () => sortRows(data?.rows ?? [], sortKey, sortDirection),
    [data?.rows, sortKey, sortDirection],
  );
  const deletedMatchCount = data?.deleted_match_count ?? 0;
  const filtersActive = query.trim() !== "" || status !== "active";

  function toggleSort(key: SupplierSortKey) {
    if (key === sortKey) {
      setSortDirection((current) => (current === "asc" ? "desc" : "asc"));
      return;
    }
    setSortKey(key);
    setSortDirection("asc");
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-4">
      <div className="flex shrink-0 flex-wrap items-center justify-between gap-3">
        <PageTitleWithHelp hoverHint="Megaventory supplier master. Deleted rows stay in the hub after a merge so purchase history can point at the surviving supplier.">
          Suppliers
        </PageTitleWithHelp>
        <Button type="button" onClick={() => navigate("/mrp/suppliers/new")}>
          Add a supplier
        </Button>
      </div>
      <Card>
        <CardHeader>
          <CardTitle>Filters</CardTitle>
        </CardHeader>
        <CardBody className="grid gap-3 md:grid-cols-2">
          <div className="grid gap-1">
            <Label htmlFor="supplier-search">Search</Label>
            <Input
              id="supplier-search"
              type="search"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Name or MV id"
            />
          </div>
          <div className="grid gap-1">
            <Label>Status</Label>
            <div className="flex flex-wrap gap-2">
              {STATUS_OPTIONS.map((option) => (
                <Button
                  key={option.value}
                  size="sm"
                  variant={status === option.value ? "primary" : "secondary"}
                  aria-pressed={status === option.value}
                  onClick={() => setStatus(option.value)}
                >
                  {option.label}
                </Button>
              ))}
            </div>
          </div>
          {query.trim() !== "" && status !== "deleted" && deletedMatchCount > 0 ? (
            <p className="text-sm text-slate-600 md:col-span-2">
              {deletedMatchCount === 1
                ? "1 deleted supplier matches this search."
                : `${deletedMatchCount} deleted suppliers match this search.`}{" "}
              <button
                type="button"
                className="text-blue-700 hover:underline"
                onClick={() => setStatus("deleted")}
              >
                Show deleted
              </button>
            </p>
          ) : null}
        </CardBody>
      </Card>
      <div className="grid min-h-[28rem] flex-1 gap-4 lg:grid-cols-[minmax(20rem,26rem)_minmax(0,1fr)] lg:items-stretch">
      <HubDataTable
        className="min-h-[28rem]"
        title={rows.length === 1 ? "1 supplier" : `${rows.length} suppliers`}
        columns={columns}
        rows={rows}
        rowKey={(row) => row.id}
        sortKey={sortKey}
        sortDir={sortDirection}
        onSort={toggleSort}
        loading={isLoading}
        error={error != null ? (error as Error).message : null}
        empty="No suppliers."
        isFiltered={filtersActive}
        filteredEmpty="No suppliers match these filters."
        onRowClick={(row) => selectSupplier(row.id)}
        rowClassName={(row) =>
          cn(selectedSupplierId === row.id && "bg-sky-50 ring-1 ring-inset ring-sky-200")
        }
        tableClassName="w-full table-fixed border-collapse text-left text-xs text-slate-800"
      />
      {selectedSupplierId == null ? (
        <Card className="flex min-h-0 flex-col">
          <CardBody className="flex flex-1 flex-col justify-center">
            <p className="text-sm text-slate-600">Select a supplier from the list.</p>
          </CardBody>
        </Card>
      ) : (
        <SupplierDetailPanel supplierId={selectedSupplierId} />
      )}
      </div>
    </div>
  );
}
